import { useCallback, useEffect, useState } from 'react';
import { apiErrorMessage } from '../api/client';
import * as formulationsApi from '../api/formulationsApi';
import { EmptyState, ErrorBanner, Modal, Skeleton } from './ui';
import {
  AdminFormulation,
  FormulationRequest,
  FormulationResponseFull,
  NoteVolume,
} from '../types/formulation';
import { formatDateTime, formatMl } from '../utils/format';

/**
 * One formulation, in full: the answers that went in and the formula that came
 * out. Shared by the cart-abandonment list and the cluster drill-down, which
 * both need exactly this panel behind their "View" button.
 *
 * The recipe is NOT here and cannot be: the service sends note names ordered by
 * prominence and nothing else. Proportions never leave the formulation service.
 */

/** Answers in the order the quiz asks them, so the panel reads like the form. */
const REQUEST_FIELDS: { key: keyof FormulationRequest; label: string }[] = [
  { key: 'personality_archetype', label: 'Personality' },
  { key: 'recharge_style',        label: 'Recharge' },
  { key: 'desired_impression',    label: 'Impression' },
  { key: 'preferred_families',    label: 'Families' },
  { key: 'comfort_memory',        label: 'Memory' },
  { key: 'climate',               label: 'Climate' },
  { key: 'fragrance_style',       label: 'Style' },
];

/** Answers arrive as strings or (for the ranked families question) arrays. */
export function answerText(value: unknown): string {
  if (Array.isArray(value)) return value.filter(Boolean).join(', ');
  if (value === null || value === undefined || value === '') return '';
  return String(value);
}

function Pills({ notes, tone }: { notes: string[]; tone: string }) {
  if (!notes.length) return <span className="text-xs text-sub">—</span>;
  return (
    <div className="flex flex-wrap gap-1.5">
      {notes.map((n) => (
        <span
          key={n}
          className={`rounded-full border px-2.5 py-1 text-xs ${tone}`}
        >
          {n}
        </span>
      ))}
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex gap-3 py-1.5">
      <span className="w-28 shrink-0 text-xs text-sub">{label}</span>
      <span className="text-sm">{value || '—'}</span>
    </div>
  );
}

export function FormulationDetailBody({ data }: { data: AdminFormulation }) {
  const score = Math.round((data.response.compatibility_score ?? 0) * 100);

  return (
    <div className="space-y-5">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-sub">Request</p>
        <p className="mb-2 text-xs text-sub/70">
          Generated {formatDateTime(data.created_at)}
          {data.user_id ? '' : ' · not signed in at the time'}
        </p>
        <div className="divide-y divide-edge rounded-xl border border-edge px-4 py-2">
          {REQUEST_FIELDS.map((f) => (
            <Row key={String(f.key)} label={f.label} value={answerText(data.request?.[f.key])} />
          ))}
        </div>
      </div>

      <div>
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-sub">Response</p>
        <div className="space-y-3 rounded-xl border border-edge p-4">
          <Row label="Cluster" value={data.response.cluster || data.cluster_label} />
          <div>
            <p className="mb-1.5 text-xs text-sub">Top notes</p>
            <Pills notes={data.response.top_notes ?? []} tone="border-gold/40 bg-gold/10 text-gold" />
          </div>
          <div>
            <p className="mb-1.5 text-xs text-sub">Heart notes</p>
            <Pills notes={data.response.heart_notes ?? []} tone="border-edge bg-edge/40 text-ink" />
          </div>
          <div>
            <p className="mb-1.5 text-xs text-sub">Base notes</p>
            <Pills notes={data.response.base_notes ?? []} tone="border-edge bg-edge/40 text-ink" />
          </div>
          <Row label="Compatibility" value={`${score}%`} />
        </div>
      </div>

      <p className="text-xs text-sub/70">
        Note names only, strongest first. The blend proportions stay in the formulation
        service and are never sent here.
      </p>
    </div>
  );
}

/** Loads one formulation by id and shows it in a dialog. */
export function FormulationDetailModal({
  id,
  onClose,
}: {
  /** Null closes the dialog — the caller keeps the selection, not this. */
  id: string | null;
  onClose: () => void;
}) {
  const [data, setData] = useState<AdminFormulation | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError('');
    try {
      setData(await formulationsApi.getFormulation(id));
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    setData(null);
    load();
  }, [load]);

  return (
    <Modal open={id !== null} title="Formulation" onClose={onClose} size="lg">
      {error ? (
        <ErrorBanner message={error} onRetry={load} />
      ) : loading || !data ? (
        <div className="space-y-3">
          <Skeleton className="h-40" />
          <Skeleton className="h-40" />
        </div>
      ) : (
        <FormulationDetailBody data={data} />
      )}
    </Modal>
  );
}

/** Shown when a drill-down has nothing to list. Kept here so both callers agree. */
export function NoFormulations() {
  return <EmptyState message="No formulations here" hint="Nothing has been generated for this yet" />;
}

/* ─── Full input/output panel ──────────────────────────────────────────────────
 *
 * The panel above shows note names because its callers only ever receive names.
 * This one is the operational view: the answers that went in and the blend that
 * came out, ml included, for the user detail page and the two funnel
 * drill-downs.
 *
 * It renders whatever the admin endpoints send. Those endpoints are the only
 * place volumes are exposed at all, and they are admin-gated at the gateway —
 * this component must not be reached from anything else.
 */

/**
 * The same order the quiz asks in, plus the dislikes it collects last.
 *
 * `empty` distinguishes a question that wasn't asked from one answered with
 * nothing: an old row predating a field shows "—", while naming no dislikes is
 * a real answer and reads "None".
 */
const IO_FIELDS: { key: keyof FormulationRequest; label: string; empty?: string }[] = [
  { key: 'personality_archetype', label: 'Personality' },
  { key: 'recharge_style',        label: 'Recharge' },
  { key: 'desired_impression',    label: 'Impression' },
  { key: 'preferred_families',    label: 'Families' },
  { key: 'comfort_memory',        label: 'Memory' },
  { key: 'climate',               label: 'Climate' },
  { key: 'fragrance_style',       label: 'Style' },
  { key: 'dislikes',              label: 'Dislikes', empty: 'None' },
];

/** "1.2 ml", or just the name when the stored row had no usable volume. */
function NoteTier({ label, notes }: { label: string; notes: NoteVolume[] }) {
  return (
    <div>
      <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-sub">{label}</p>
      {notes.length === 0 ? (
        <span className="text-xs text-sub">—</span>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {notes.map((n) => (
            <span
              key={n.name}
              className="inline-flex items-baseline gap-1.5 rounded-full border border-gold/40 bg-gold/10 px-2.5 py-1 text-xs text-gold"
            >
              {n.name}
              {n.ml !== null && n.ml !== undefined && (
                <span className="tabular-nums text-gold/70">{formatMl(n.ml)}</span>
              )}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function IORow({ label, value, empty = '—' }: { label: string; value: string; empty?: string }) {
  return (
    <div className="flex gap-3 py-1.5">
      <span className="w-24 shrink-0 text-xs text-sub">{label}</span>
      <span className="break-words text-sm">{value || <span className="text-sub">{empty}</span>}</span>
    </div>
  );
}

/**
 * One formulation in full: INPUT (their answers) then OUTPUT (their blend).
 *
 * `request` / `response` are optional because an order can outlive the trail
 * back to its formula — see ConvertedOrder. The caller gets a plain note rather
 * than an empty frame.
 */
export function FormulationIO({
  request,
  response,
  clusterLabel,
}: {
  request?: FormulationRequest | null;
  response?: FormulationResponseFull | null;
  clusterLabel?: string | null;
}) {
  if (!request && !response) {
    return (
      <p className="rounded-xl border border-edge px-4 py-3 text-xs text-sub">
        This order could not be traced back to a formulation — the line predates the link
        between an order and the formula it was blended from.
      </p>
    );
  }

  const score = Math.round((response?.compatibility_score ?? 0) * 100);

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <div>
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-sub">
          Input <span className="font-normal normal-case text-sub/70">— what they answered</span>
        </p>
        <div className="divide-y divide-edge rounded-xl border border-edge px-4 py-2">
          {IO_FIELDS.map((f) => (
            <IORow
              key={String(f.key)}
              label={f.label}
              value={answerText(request?.[f.key])}
              empty={f.empty}
            />
          ))}
        </div>
      </div>

      <div>
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-sub">
          Output <span className="font-normal normal-case text-sub/70">— what they got</span>
        </p>
        <div className="space-y-3 rounded-xl border border-edge p-4">
          <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-edge pb-3">
            <span className="text-sm font-medium text-gold">
              {response?.cluster || clusterLabel || '—'}
            </span>
            <span className="text-xs text-sub">
              Compatibility <span className="font-semibold tabular-nums text-ink">{score}%</span>
            </span>
          </div>
          <NoteTier label="Top notes"   notes={response?.top_notes ?? []} />
          <NoteTier label="Heart notes" notes={response?.heart_notes ?? []} />
          <NoteTier label="Base notes"  notes={response?.base_notes ?? []} />
        </div>
      </div>
    </div>
  );
}
